import { useCallback, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useAdk, useApiService } from "@art/shared-ui";
import { notifications } from "@art/adk-notifications";
import {
  setInbox,
  setUnreadCount,
  receiveNotification,
  markItemRead,
  markAllRead,
  InboxItem,
} from "../../store/slice/inboxSlice";

const NOTIFICATION_CHANNEL = "art_notifications";
const NOTIFICATION_EVENT = "notification.new";

function toInboxItem(raw: any): InboxItem | null {
  const c = raw && (raw.id || raw.title) ? raw : raw?.content ?? raw?.data;
  if (!c || !c.id) return null;
  return {
    id: c.id,
    type: c.type ?? "",
    title: c.title ?? "",
    body: c.body ?? "",
    data: c.data,
    status: (c.status as InboxItem["status"]) ?? "unread",
    created_at: c.created_at ?? "",
  };
}

export function useInbox() {
  const adk = useAdk() as any;
  const apiService = useApiService();
  const dispatch = useDispatch();

const apiBaseUrl = import.meta.env.PUBLIC_BASE_URL as string | undefined;
  const notif = useMemo(() => {
    if (!adk) return null;
    if (!adk.notifications && typeof adk.use === "function") {
      adk.use(notifications({ apiBaseUrl }));
    }
    return adk.notifications ?? null;
  }, [adk, apiBaseUrl]);

  const hydrate = useCallback(async () => {
    try {
      if (notif) {
        const { notifications: list } = await notif.list({ limit: 20 });
        dispatch(setInbox((list ?? []).map(toInboxItem).filter(Boolean) as InboxItem[]));
        dispatch(setUnreadCount(await notif.unreadCount()));
      } else if (apiService) {
        const res = await apiService.get("/notifications?limit=20", {showErrorSnack: false, showSuccessSnack: false});
        const list = res?.data?.data?.notifications ?? [];
        dispatch(setInbox(list.map(toInboxItem).filter(Boolean) as InboxItem[]));
        const countRes = await apiService.get("/notifications/unread-count", {showErrorSnack: false, showSuccessSnack: false});
        dispatch(setUnreadCount(countRes?.data?.data?.unread_count ?? 0));
      }
    } catch (e) {
      console.error("[notifications] hydrate failed", e);
    }
  }, [notif, apiService, dispatch]);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (!adk) return;
    let cancelled = false;
    let off: (() => void) | undefined;
    let subscription: any;

    const onItem = (payload: any) => {
      const item = toInboxItem(payload);
      if (item) dispatch(receiveNotification(item));
    };

    (async () => {
      try {
        if (notif) {
          off = await notif.onNew(onItem);
        } else {
          subscription = await adk.subscribe(NOTIFICATION_CHANNEL);
          if (cancelled) {
            subscription?.unsubscribe?.();
            return;
          }
          subscription.bind(NOTIFICATION_EVENT, onItem);
        }
      } catch (e) {
        console.error("[notifications] live subscribe failed", e);
      }
    })();

    return () => {
      cancelled = true;
      try {
        off?.();
        subscription?.remove?.(NOTIFICATION_EVENT, onItem);
      } catch {
      }
    };
  }, [adk, notif, dispatch]);

  const markOne = useCallback(
    async (id: string) => {
      try {
        if (notif) await notif.markRead([id]);
        else await apiService?.post("/notifications/mark-read", { ids: [id] });
        dispatch(markItemRead(id));
      } catch (e) {
        console.error("[notifications] mark read failed", e);
      }
    },
    [notif, apiService, dispatch]
  );

  const markAll = useCallback(async () => {
    try {
      if (notif) await notif.markRead();
      else await apiService?.post("/notifications/mark-read", { all: true });
      dispatch(markAllRead());
    } catch (e) {
      console.error("[notifications] mark all read failed", e);
    }
  }, [notif, apiService, dispatch]);


  return { markOne, markAll, refresh: hydrate };
}
